import React from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Text, Label } from 'recharts';

import { Divider } from 'uiToolkitV1/Divider';
import { useExpandableContainer } from 'uiToolkitV1/ExpandableContainerProvider';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

const AxisLabel = ({ axisType, value, viewBox }) => {
  const { x, y, width, height } = viewBox;
  const theme = useTheme();

  const isVert = axisType === 'yAxis';

  const defaultOffset = 26;

  const cx = isVert ? defaultOffset : width / 2 + x;
  const cy = isVert ? height / 2 + y : y + height + defaultOffset;

  return (
    <Text dx={cx} dy={cy} angle={isVert ? -90 : 0} textAnchor="middle" fontSize="12px" fill={theme.colors.text.g2}>
      {value}
    </Text>
  );
};

const FORMS_LABEL = {
  PERFORMANCE: 'Performance',
  QNA: 'QNA',
  OVERALL: 'Overall',
  POTENTIAL: 'Potential',
};
const TooltipContent = ({ payload = [], metaDataKey, metaDataLabelMap }) => {
  if (!payload) {
    return null;
  }
  const formWiseScore = payload[0]?.payload[metaDataKey] ?? {};
  const formWiseScoreFormattedData = Object.keys(formWiseScore)
    .map((label) => ({
      label,
      value: formWiseScore[label],
    }))
    .filter(({ label }) => label !== 'OVERALL');

  const reviewData = payload?.[0]?.payload;

  return (
    <PaperWithShadow p={2}>
      <Box py={1} px={2}>
        <TextBodyMedium bold>{reviewData?.reviewCycle}</TextBodyMedium>
        {reviewData?.publishedOn && (
          <TextLabel color="text.g2" mt="2px">
            {format(reviewData.publishedOn, 'MMM d, y')}
          </TextLabel>
        )}
      </Box>

      {reviewData?.overallRating && (
        <>
          <Divider color="neutral.F2F2F2" my={1} />
          <Flex justify="space-between" p="2px 8px">
            <TextBodyMedium bold>{metaDataLabelMap.OVERALL}</TextBodyMedium>
            <TextBodyMedium bold ml={4}>
              {reviewData?.overallRating}
            </TextBodyMedium>
          </Flex>
        </>
      )}

      {formWiseScoreFormattedData?.length && (
        <>
          <Divider color="neutral.F2F2F2" my={1} />
          {formWiseScoreFormattedData?.map(({ label, value }) => (
            <Flex justify="space-between" p="2px 8px" key={label}>
              <TextBodyMedium>{FORMS_LABEL[label]}</TextBodyMedium>
              <TextBodyMedium ml={4}>{value}</TextBodyMedium>
            </Flex>
          ))}
        </>
      )}
    </PaperWithShadow>
  );
};

const CustomLineChartLabel = (props) => {
  const { index, x, y } = props;

  return (
    <g>
      <foreignObject className="node" x={x - 25} y={y - 7} width={50} height={20}>
        <text className="recharts-text recharts-cartesian-axis-tick-value" stroke="none" fill="#666">
          <TextLabelSmall
            sx={{
              background: index === 0 ? 'text.g2' : 'button.blue.default',
              color: 'common.white',
              textAlign: 'center',
            }}
            bold
          >
            {index === 0 ? 'Past' : 'Current'}
          </TextLabelSmall>
        </text>
      </foreignObject>
    </g>
  );
};

const PreviewPerformanceLineChart = ({
  data = [],
  linesInfo = [
    {
      key: '',
      strokeDasharray: '',
      color: '',
    },
  ],
  xAxisKey = 'ts',
  yAxisKey = '',
  xAxisTickFormatter,

  xAxisLabel = 'Time',
  xAxisDomain = ['auto', 'auto'],
  yAxisDomain = ['auto', 'auto'],

  yAxisTicks = [],
  yAxisLabel = 'Average Progress',

  xAxisType = 'number',
  yAxisType = 'number',
  metaDataKey,
  metaDataLabelMap,
  height,
  hideYAxisLabelTicks = false,
}) => {
  const { isExpanded = false } = useExpandableContainer();

  return (
    <ResponsiveContainer minHeight={isExpanded ? 600 : 255} height={isExpanded ? 600 : 333}>
      <LineChart
        data={data}
        margin={{
          top: 24,
          right: 50,
          left: 30,
          bottom: 40,
        }}
        style={{ zIndex: '1', position: 'absolute' }}
        height={height}
      >
        <XAxis
          type={xAxisType}
          dataKey={xAxisKey}
          domain={xAxisDomain}
          tickFormatter={xAxisTickFormatter}
          tickSize={0}
          tickMargin={16}
          padding={{ left: 50, right: 50 }}
          stroke="#CCCCCC"
        >
          <Label content={<AxisLabel axisType="xAxis" value={xAxisLabel} />} />
        </XAxis>
        <YAxis
          display={hideYAxisLabelTicks && 'none'}
          type={yAxisType}
          tickMargin={24}
          dataKey={hideYAxisLabelTicks ? '' : yAxisKey}
          ticks={hideYAxisLabelTicks ? [] : yAxisTicks}
          name={hideYAxisLabelTicks ? 'Overall →' : 'Performance →'}
          domain={!hideYAxisLabelTicks && yAxisDomain}
          stroke="#CCCCCC"
        >
          <Label content={<AxisLabel axisType="yAxis" value={hideYAxisLabelTicks ? '' : yAxisLabel} />} />
        </YAxis>
        <Tooltip
          wrapperStyle={{ outline: 'none' }}
          content={<TooltipContent metaDataKey={metaDataKey} metaDataLabelMap={metaDataLabelMap} />}
        />

        {linesInfo?.map(({ key, color, strokeDasharray }) => (
          <Line
            connectNulls
            strokeDasharray={strokeDasharray}
            dataKey={key}
            stroke={color}
            dot={{ r: 3, strokeWidth: 1, strokeDasharray: '', fill: color }}
            activeDot={false}
            key={key}
            label={!isExpanded && <CustomLineChartLabel />}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default PreviewPerformanceLineChart;
